<template>
  <ReleaseAnnouncementDialog
    v-if="display"
    :key="display.key"
    :model-value="visible"
    :announcement="display.announcement"
    acknowledgement-required
    :acknowledging="acknowledging"
    :acknowledgement-error="acknowledgementError"
    @update:model-value="visible = $event"
    @opened="display.onOpened"
    @acknowledge="display.onAcknowledge"
  />
  <div v-if="fallbackVisible" class="release-fallback-notice" role="status">
    <span>暂时没有可展示的更新公告，您可以</span>
    <a :href="DEFAULT_RELEASE_LOG_URL" target="_blank" rel="noopener noreferrer" @click="fallbackVisible = false">查看完整更新日志</a>
    <button type="button" aria-label="关闭提示" @click="fallbackVisible = false">×</button>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import ReleaseAnnouncementDialog from '@/components/ReleaseAnnouncementDialog.vue'
import { getCurrentRelease, claimRelease, markReleaseShown, acknowledgeRelease } from '@/api/releaseAnnouncements'
import { DEFAULT_RELEASE_LOG_URL } from '@/utils/releaseAnnouncements'

const props = defineProps({
  userId: { type: [String, Number], default: null },
  authenticated: { type: Boolean, default: false },
  onboardingResolved: { type: Boolean, default: false },
  businessPage: { type: Boolean, default: false },
  routeKey: { type: String, default: '' },
  blocking: { type: Boolean, default: false },
})

const visible = ref(false)
const display = shallowRef(null)
const documentVisible = ref(document.visibilityState !== 'hidden')
const otherModalVisible = ref(false)
const manualPending = ref(false)
const fallbackVisible = ref(false)
const acknowledging = ref(false)
const acknowledgementError = ref('')
let generation = 0
let interaction = 0
let displaySequence = 0
let inflight = null
let pendingCheck = false
let checkedRoute = null
let disposed = false
let modalObserver = null
const attemptedIds = new Set()
const accountId = computed(() => props.userId == null ? '' : String(props.userId))
const ready = computed(() => Boolean(
  accountId.value && props.authenticated && props.onboardingResolved && props.businessPage &&
  documentVisible.value && !props.blocking && !otherModalVisible.value && !visible.value && !manualPending.value
))

function context() {
  return { accountId: accountId.value, generation, interaction }
}

function isCurrent(request) {
  if (disposed || request.generation !== generation || request.accountId !== accountId.value || !props.authenticated) return false
  // storage 事件/父组件更新可能晚于用户操作；回调前再核对真实登录账号，不能记到新账号。
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || 'null')
    return Boolean(localStorage.getItem('token') && storedUser?.id != null && String(storedUser.id) === request.accountId)
  } catch {
    return false
  }
}

function readData(response) {
  if (response?.data?.code !== 200) throw new Error('更新公告暂不可用')
  return response.data.data
}

function report(request, entry, action) {
  if (!entry.automatic || display.value !== entry || !isCurrent(request)) return
  // 展示回调失败不会重新领取或重新弹出；下次浏览业务页面仍由服务端记录去重。
  Promise.resolve(action(entry.announcement.id)).catch(() => {})
}

async function acknowledge(request, entry) {
  if (acknowledging.value || display.value !== entry || !visible.value || !isCurrent(request)) return
  acknowledging.value = true
  acknowledgementError.value = ''
  try {
    readData(await acknowledgeRelease(entry.announcement.id))
    if (!isCurrent(request)) return
    window.dispatchEvent(new Event('biointelos-messages-updated'))
    if (display.value === entry) visible.value = false
  } catch {
    if (display.value === entry && visible.value && isCurrent(request)) acknowledgementError.value = '已读状态未保存，请重试。'
  } finally {
    if (display.value === entry) acknowledging.value = false
  }
}

function showAnnouncement(announcement, request, automatic) {
  const entry = {
    key: `${request.generation}:${++displaySequence}`,
    announcement,
    automatic,
  }
  // 捕获此次打开的账号/版本，而不是在动画回调发生时读取可能已切换的弹窗。
  entry.onOpened = () => report(request, entry, markReleaseShown)
  entry.onAcknowledge = () => acknowledge(request, entry)
  acknowledging.value = false
  acknowledgementError.value = ''
  display.value = entry
  visible.value = true
}

async function checkCurrent() {
  if (!ready.value || checkedRoute === props.routeKey) return
  if (inflight) {
    pendingCheck = true
    return
  }
  const request = context()
  const operation = { request }
  inflight = operation
  try {
    const result = readData(await getCurrentRelease())
    syncModalState()
    if (!isCurrent(request) || request.interaction !== interaction || !ready.value) return
    checkedRoute = props.routeKey
    const announcement = result?.announcement
    if (!announcement || !result.canAutoShow || attemptedIds.has(String(announcement.id))) return

    // 领取一旦发出就不在本会话重试，避免响应丢失时误以为没有消费展示资格。
    attemptedIds.add(String(announcement.id))
    const claim = readData(await claimRelease(announcement.id))
    syncModalState()
    if (!isCurrent(request) || request.interaction !== interaction || !ready.value) return
    if (claim?.shouldShow && claim.announcement) showAnnouncement(claim.announcement, request, true)
  } catch {
    // 公告是附加信息，弱网和后台故障不阻塞用户的研究流程。后续页面进入可重查。
  } finally {
    if (inflight === operation) {
      inflight = null
      // 只补上进行中被合并的路由/就绪变化，不因接口失败自行循环重试。
      if (pendingCheck) {
        pendingCheck = false
        void checkCurrent()
      }
    }
  }
}

async function openCurrent() {
  if (!props.authenticated || !accountId.value || manualPending.value) return
  if (visible.value) return
  interaction += 1
  fallbackVisible.value = false
  const request = context()
  checkedRoute = props.routeKey
  manualPending.value = true
  try {
    const result = readData(await getCurrentRelease())
    if (!isCurrent(request) || request.interaction !== interaction) return
    if (result?.announcement) {
      // 人工查看只读；仅在当前页面会话避免刚关掉后立刻自动再弹，不写用户记录。
      attemptedIds.add(String(result.announcement.id))
      checkedRoute = props.routeKey
      showAnnouncement(result.announcement, request, false)
    } else {
      fallbackVisible.value = true
    }
  } catch {
    if (isCurrent(request) && request.interaction === interaction) {
      fallbackVisible.value = true
    }
  } finally {
    if (request.generation === generation && request.interaction === interaction) manualPending.value = false
  }
}

function hasVisibleModal(element) {
  if (element.closest('.release-announcement-overlay, .release-announcement-dialog')) return false
  for (let ancestor = element; ancestor instanceof Element; ancestor = ancestor.parentElement) {
    const style = window.getComputedStyle(ancestor)
    if (ancestor.hidden || style.display === 'none' || style.visibility === 'hidden') return false
  }
  return true
}

function syncModalState() {
  otherModalVisible.value = Array.from(document.querySelectorAll('.el-overlay, dialog[open], [role="dialog"][aria-modal="true"]'))
    .some(hasVisibleModal)
}

function onVisibilityChange() {
  documentVisible.value = document.visibilityState !== 'hidden'
}

watch([accountId, () => props.authenticated], () => {
  generation += 1
  interaction += 1
  visible.value = false
  display.value = null
  manualPending.value = false
  fallbackVisible.value = false
  acknowledging.value = false
  acknowledgementError.value = ''
  inflight = null
  pendingCheck = false
  checkedRoute = null
  attemptedIds.clear()
}, { flush: 'sync' })

watch(() => props.businessPage, businessPage => {
  if (!businessPage) {
    interaction += 1
    visible.value = false
    display.value = null
    manualPending.value = false
    fallbackVisible.value = false
  }
}, { flush: 'sync' })

watch([accountId, ready, () => props.routeKey], () => { void checkCurrent() }, { flush: 'post' })

onMounted(() => {
  syncModalState()
  modalObserver = new MutationObserver(syncModalState)
  modalObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'open', 'aria-modal'] })
  document.addEventListener('visibilitychange', onVisibilityChange)
  void checkCurrent()
})

onUnmounted(() => {
  disposed = true
  modalObserver?.disconnect()
  document.removeEventListener('visibilitychange', onVisibilityChange)
})

defineExpose({ openCurrent })
</script>

<style scoped>
.release-fallback-notice { position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%); z-index: 2100; display: flex; align-items: center; flex-wrap: wrap; gap: 8px; width: max-content; max-width: calc(100vw - 40px); box-sizing: border-box; padding: 13px 16px; border: 1px solid #dce4f6; border-radius: 10px; background: white; color: #69778f; box-shadow: 0 8px 28px #17233d1a; font-size: 13px; line-height: 1.6; }
.release-fallback-notice a { color: #0840e6; }
.release-fallback-notice button { border: 0; background: none; color: #69778f; font-size: 20px; cursor: pointer; padding: 0 5px; }
</style>
